import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(advancedFormat);
dayjs.extend(utc);
dayjs.extend(timezone);

export type DateInput = string | number | Date | null | undefined;

interface FormatOptions {
  fallback?: string;
  tz?: string;
  locale?: string;
}

/** Safe dayjs format — returns fallback on null/undefined/invalid */
function safeDayjsFormat(value: DateInput, formatStr: string, opts: FormatOptions = {}): string {
  const { fallback = '', tz, locale } = opts;
  if (value == null || value === '') return fallback;
  try {
    let d = tz ? dayjs(value).tz(tz) : dayjs(value);
    if (!d.isValid()) return fallback;
    if (locale) d = d.locale(locale);
    return d.format(formatStr);
  } catch {
    return fallback;
  }
}

/** "4/3/26, 10:33 AM" — compact for grid display */
export function formatDateShort(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'M/D/YY, h:mm A', { fallback });
}

/** "Apr 03, 2026 at 10:33 AM" */
export function formatDateTimeLong(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMM DD, YYYY [at] h:mm A', { fallback });
}

/** "April 3, 2026" — long date, no time */
export function formatDateLong(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMMM D, YYYY', { fallback });
}

/** "Apr 3, 2026" — medium date, no time */
export function formatDateMedium(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MMM D, YYYY', { fallback });
}

/** "04/03/2026, 10:33 AM" — compact with time */
export function formatDateTimeCompact(value: DateInput, fallback = ''): string {
  return safeDayjsFormat(value, 'MM/DD/YYYY, hh:mm A', { fallback });
}

/** "Apr 3, 2026, 10:33:09 AM" — with seconds */
export function formatDateTimeWithSeconds(
  value: DateInput,
  fallback = '',
  locale?: string,
): string {
  return safeDayjsFormat(value, 'MMM D, YYYY, hh:mm:ss A', { fallback, locale });
}
